import { Moment } from 'moment';
import HIIVoyageExpense from '@shared/hireInvoice/HIIVoyageExpense';
import HIIBunker from '@shared/hireInvoice/HIIBunker';
import HIIFreight from '@shared/hireInvoice/HIIFreight';
import HIIOffHirePeriod from '@shared/hireInvoice/HIIOffHirePeriod';
import HIIOffHireBunker from '@shared/hireInvoice/HIIOffHireBunker';
import HIIOffHireExpenseSubjectToHireDays from '@shared/hireInvoice/HIIOffHireExpenseSubjectToHireDays';
import HIIOffHireAddressCommission from '@shared/hireInvoice/HIIOffHireAddressCommission';
import HIIOffHireBrokerCommission from '@shared/hireInvoice/HIIOffHireBrokerCommission';
import HireInvoiceItem, { TemplateItemChildren } from '@shared/hireInvoice/HireInvoiceItem';
import HIIFreightAddressCommission from '@shared/hireInvoice/HIIFreightAddressCommission';
import HIIFreightBrokerCommission from '@shared/hireInvoice/HIIFreightBrokerCommission';
import HIIDeadfreight from '@shared/hireInvoice/HIIDeadfreight';
import HIIOverage from '@shared/hireInvoice/HIIOverage';
import HIIDespatch from '@shared/hireInvoice/HIIDespatch';
import HIIDemurrage from '@shared/hireInvoice/HIIDemurrage';
import { getCargoPortLaytimeStats } from '@shared/utils/getCargoPortLaytimeStats';
import { splitActionKey } from '@shared/utils/splitActionKey';
import { formatCurrency } from '@shared/utils/currency';
import { getLaytimeInvoiceLineDescriptions } from '@shared/utils/getLaytimeInvoiceLineDescriptions';
import HIIDemurrageBrokerCommission from '@shared/hireInvoice/HIIDemurrageBrokerCommission';
import HIIDemurrageAddressCommission from '@shared/hireInvoice/HIIDemurrageAddressCommission';
import HIIRevenueItem from '@shared/hireInvoice/HIIRevenueItem';
import HIIBbItem from '@shared/hireInvoice/HIIBbItem';
import HIIBbItemAddressCommission from '@shared/hireInvoice/HIIBbItemAddressCommission';
import HIIBbItemBrokerCommission from '@shared/hireInvoice/HIIBbItemBrokerCommission';
import type { BunkerProps } from '@api/models/bunker';
import type { VoyageOffHirePeriodProps } from '@api/models/voyage-off-hire-period';
import type { ExpenseSubjectToHireDaysProps } from '@api/models/expense-subject-to-hire-days';
import type {
	CargoStats,
	CargoWithPortsAndCalculationsJSON,
} from '@api/features/laytime/getCargosAndLaytimes';
import type { VoyageOffHireBunkerProps } from '@api/models/voyage-off-hire-bunker';
import type { Port } from '@api/utils/ports';
import type { VoyageInvoice } from '@api/utils/sequelize/getAllVoyageInvoices';
import { RevenueItemProps } from '@api/models/revenue-item';
import { OffHirePeriodProps } from '@api/models/off-hire-period';
import { VoyageExpenseReceivableProps } from '@api/models/voyage-expense-receivable';
import { BbItemProps } from '@api/models/bb-item';
import { CargoProps } from '@api/models/cargo';
import { BrokerProps } from '@api/models/broker';
import BrokerInCargo, { BrokerInCargoProps } from '@api/models/broker-in-cargo';
import CargoPort from '@api/models/cargo-port';
import { VoyageBunkerEntry } from '@api/utils/sequelize/getVoyageBunkers';
import type { Values } from './objectEnums';
import {
	AccountTypes,
	CalculationMethodForExpensesSubjectToHireDays,
	Currencies,
	LaytimeIntervals,
	LaytimeTerms,
	TimeZoneFormat,
} from './constants';
import { dndRateUnitLabel } from './laytime';
import { ensureNegative } from './math';

export type ChartererBroker = {
	id: number;
	name: string;
	commission: number | null;
};

type DndLaytimeProps = {
	getId: () => number;
	cargo: CargoWithPortsAndCalculationsJSON;
	fixtureCurrency: Values<typeof Currencies>;
	laytimeStats: CargoStats;
	items: (HIIDemurrage | HIIDespatch)[];
};

export const flattenHireInvoiceItems = (items: HireInvoiceItem[]) => items?.reduce<HireInvoiceItem[]>((arr, i) => [
	...arr,
	i,
	...(i.children || []),
], []) || [];

export const getFullHireInvoiceItemType = (hireInvoiceItem: HireInvoiceItem, items: HireInvoiceItem[]) => {
	const parent = hireInvoiceItem.getParent(items);

	if (hireInvoiceItem.parentId == null || parent == null) {
		return hireInvoiceItem.itemTypeLabel;
	}

	return `${parent.itemTypeLabel} ➝ ${hireInvoiceItem.itemTypeLabel}`;
};

export const getExpenseHireInvoiceItem = (
	getId: () => number,
	expense: VoyageExpenseReceivableProps,
	fixtureCurrency: Values<typeof Currencies>,
) => (
	new HIIVoyageExpense(getId(), true, {
		voyageExpenseReceivableId: expense.id,
		amount: expense.amount,
		invoiceDescription: expense.itemDescription,
		account: expense.account,
		currency: expense.currency,
		exchangeRate: expense.exchangeRate,
		fixtureCurrency,
		unit: expense.unit,
		quantity: expense.quantity,
		unitPrice: expense.unitPrice,
	})
);

const getVoyageBunkerNote = (bunker: VoyageBunkerEntry) => {
	if (!bunker.BunkerStem) {
		return bunker.note;
	}

	const matchingParcel = bunker?.BunkerStem?.BunkerStemParcels
		?.find((parcel) => (
			parcel.liftedPPT === bunker.Bunker.pricePerTon &&
			parcel.fuelGrade === bunker.Bunker.fuelGrade)
		);

	if (!matchingParcel) {
		return bunker.note;
	}

	return `
		From stem in
		${bunker.BunkerStem.port?.name} - 
		${bunker.BunkerStem.stemDate} - 
		${matchingParcel.quantity}MT @ ${formatCurrency(matchingParcel.liftedPPT, matchingParcel.currency)} / MT`;
};

export const getBunkerHireInvoiceItem = (
	getId: () => number,
	bunker: VoyageBunkerEntry,
	fixtureCurrency: Values<typeof Currencies>,
) => (
	new HIIBunker(getId(), true, {
		bunkerId: bunker.id,
		quantity: bunker.quantity,
		pricePerTon: bunker.pricePerTon,
		currency: bunker.currency,
		exchangeRate: bunker.exchangeRate,
		fuelGrade: bunker.fuelGrade,
		type: bunker.type,
		note: getVoyageBunkerNote(bunker),
		fixtureCurrency,
	})
);
//
export const getFreightHireInvoiceItem = (
	getId: () => number,
	cargo: CargoWithPortsAndCalculationsJSON,
	fixtureCurrency: Values<typeof Currencies>,
	params: {
		percentage: number;
		blQuantity: number;
		totalBlQuantity?: number | null;
		addressCommissionInvoicablePercentage: number;
		brokerCommissionInvoicablePercentage: number;
		maxAddressCommissionPercentage: number;
		maxBrokerCommissionPercentage: number;
	},
) => {
	const freightItem = new HIIFreight(getId(), true, {
		cargoId: cargo.id,
		quantity: params.blQuantity,
		cargoType: cargo.type,
		freightRate: cargo.freightRate,
		freightType: cargo.freightType,
		charterer: cargo.charterer,
		exchangeRate: cargo.exchangeRate,
		currency: cargo.currency,
		unit: cargo.unit,
		fixtureCurrency,
		addressCommission: cargo.addressCommission,
		brokerCommission: cargo.brokerCommission,
		maxPercentage: params.percentage ?? 100,
		percentage: params.percentage ?? 100,
		note: '',
		cargoPorts: cargo.CargoPorts as unknown as CargoPort[],
		cpDate: cargo.cpDate,
		blNumber: cargo.blNumber ?? '',
		blQuantity: params.totalBlQuantity ?? params.blQuantity,
	});

	freightItem.children.push(
		...(cargo.addressCommission > 0 ? [new HIIFreightAddressCommission(getId(), true, {
			cargoId: cargo.id,
			percentage: cargo.addressCommission,
			maxPercentage: params.maxAddressCommissionPercentage,
			invoiceablePercentage: params.addressCommissionInvoicablePercentage,
			fixtureCurrency,
		})] : []),
		...cargo.Brokers
			.filter((b) => {
				return b.BrokerInCargo.paidBy === AccountTypes.CHARTERER;
			})
			.map((c) => new HIIFreightBrokerCommission(getId(), true, {
				cargoId: cargo.id,
				brokerId: c.id,
				brokerName: c.name,
				percentage: c.BrokerInCargo.commission,
				invoiceablePercentage: params.brokerCommissionInvoicablePercentage,
				maxPercentage: params.maxBrokerCommissionPercentage,
				fixtureCurrency,
			})),
	);

	freightItem.children.forEach((c) => {
		c.parentId = freightItem.id;
	});

	return freightItem;
};

export const getDeadfreightOrOverageHireInvoiceItem = (
	getId: () => number,
	cargo: CargoProps & { Brokers: Array<BrokerProps & { BrokerInCargo: BrokerInCargoProps }> },
	fixtureCurrency: Values<typeof Currencies>,
	params: {
		percentage: number;
		quantity: number;
		type: 'deadfreight' | 'overage';
		addressCommissionInvoicablePercentage: number;
		brokerCommissionInvoicablePercentage: number;
		maxAddressCommissionPercentage: number;
		maxBrokerCommissionPercentage: number;
	},
) => {
	const { type } = params;

	if ((type === 'deadfreight' && cargo.deadfreightAmount == null) ||
		(type === 'overage' && cargo.overageAmount == null)
	) {
		return null;
	}

	const attributes = {
		cargoId: cargo.id,
		quantity: params.quantity,
		cargoType: cargo.type,
		freightRate: type === 'deadfreight' ? (cargo.deadfreightAmount ?? 0) : (cargo.overageAmount ?? 0),
		freightType: cargo.freightType,
		charterer: cargo.charterer,
		exchangeRate: cargo.exchangeRate,
		currency: cargo.currency,
		unit: cargo.unit,
		fixtureCurrency,
		addressCommission: cargo.addressCommission,
		brokerCommission: cargo.brokerCommission,
		maxPercentage: params.percentage ?? 100,
		percentage: params.percentage ?? 100,
		note: '',
	};

	let item: HIIDeadfreight | HIIOverage;

	if (type === 'deadfreight') {
		item = new HIIDeadfreight(getId(), true, attributes);
	} else {
		item = new HIIOverage(getId(), true, attributes);
	}

	// Using freight for commissions until issues arise
	item.children.push(
		...(cargo.addressCommission > 0 ? [new HIIFreightAddressCommission(getId(), true, {
			cargoId: cargo.id,
			percentage: cargo.addressCommission,
			invoiceablePercentage: params.addressCommissionInvoicablePercentage,
			maxPercentage: params.maxAddressCommissionPercentage,
			fixtureCurrency,
		})] : []),
		...cargo.Brokers
			.filter((b) => {
				return b.BrokerInCargo.paidBy === AccountTypes.CHARTERER;
			})
			.map((c) => new HIIFreightBrokerCommission(getId(), true, {
				cargoId: cargo.id,
				brokerId: c.id,
				brokerName: c.name,
				percentage: c.BrokerInCargo.commission,
				invoiceablePercentage: params.brokerCommissionInvoicablePercentage,
				maxPercentage: params.maxBrokerCommissionPercentage,
				fixtureCurrency,
			})),
	);

	item.children.forEach((c) => {
		c.parentId = item.id;
	});

	item.children.forEach((c) => {
		c.parentId = item.id;
	});

	return item;
};

const isDndLineInvoiced = ({
	cargo,
	hireInvoices,
	chartererId,
	item,
}: {
	cargo: CargoWithPortsAndCalculationsJSON;
	hireInvoices: VoyageInvoice[];
	chartererId: number;
	item: HIIDemurrage | HIIDespatch;
}) => hireInvoices
	.filter((h) => h.chartererId === chartererId)
	.flatMap((invoice) => invoice.items as unknown as Array<(HIIDemurrage | HIIDespatch) & { __itemType: string }>)
	.some((i) => {
		if (
			i.__itemType !== item.getItemType() ||
			i.isReversal ||
			i.isAmendment ||
			i.cargoId !== cargo.id ||
			i.terms !== item.terms
		) {
			return false;
		}

		switch (item.terms) {
			case LaytimeTerms.NON_REVERSIBLE:
				return i.cargoPortId === item.cargoPortId;

			case LaytimeTerms.AVERAGED:
				return i.type === item.type;

			default:
				return true;
		}
	});

const getReversibleDndItems = ({
	getId,
	cargo,
	items,
	laytimeStats,
	fixtureCurrency,
}: DndLaytimeProps) => {
	const {
		totalDemurrage,
		totalDespatch,
	} = laytimeStats;

	const intervals = {
		demurrageDespatchInterval: cargo.demurrageDespatchInterval,
		timeAllowedInterval: cargo.timeAllowedInterval,
	};

	if (laytimeStats.totalReceivable === 0 && laytimeStats.totalDemurrage === 0 && laytimeStats.totalDespatch === 0) {
		return;
	}

	if (totalDemurrage && totalDemurrage > 0) {
		const item = getDemurrageInvoiceItem(getId, cargo, fixtureCurrency, {
			action: null,
			amount: totalDemurrage,
			rate: cargo.demurrage ?? 0,
			hours: Math.abs(laytimeStats.totalTimeDiff) / 60,
			port: null,
			laytimeDetails: getLaytimeInvoiceLineDescriptions(cargo),
			isSettlement: laytimeStats.isSettlement,
			...intervals,
		}) as HIIDemurrage;

		items.push(item);
	} else if (totalDespatch && Math.abs(totalDespatch) > 0) {
		let des = ensureNegative(totalDespatch);

		const item = getDespatchInvoiceItem(getId, cargo, fixtureCurrency, {
			action: null,
			amount: des,
			rate: cargo.despatch ?? 0,
			hours: Math.abs(laytimeStats.totalTimeDiff) / 60,
			port: null,
			laytimeDetails: getLaytimeInvoiceLineDescriptions(cargo),
			isSettlement: laytimeStats.isSettlement,
			...intervals,
		}) as HIIDemurrage;

		items.push(item);
	}
};

const getNonReversibleDndItems = ({
	getId,
	cargo,
	items,
	fixtureCurrency,
}: DndLaytimeProps) => {
	cargo.CargoPorts.forEach((cp) => {
		let item: HIIDemurrage | HIIDespatch | null = null;

		const {
			totalDemurrage,
			totalDespatch,
			timeGainedOrLost,
			isDem,
			isSettlement,
		} = getCargoPortLaytimeStats(cp, cargo);

		const { action } = splitActionKey(cp.portAndActionKey);

		const intervals = {
			demurrageDespatchInterval: cargo.demurrageDespatchInterval,
			timeAllowedInterval: cp.laytimeInterval,
		};

		let laytimeDetails: Array<{ section: string; columns: string[] }> = [];

		if (!isSettlement) {
			laytimeDetails = [{
				section: 'Rate',
				columns: [
					`${formatCurrency(
						isDem ? (cp.demurrage ?? 0) : (cp.despatch ?? 0), cargo.currency,
					)} ${dndRateUnitLabel(cargo.demurrageDespatchInterval)}`,
				],
			}];
		}

		if (isDem && totalDemurrage > 0) {
			item = getDemurrageInvoiceItem(getId, cargo, fixtureCurrency, {
				action,
				amount: totalDemurrage,
				rate: cp.demurrage ?? 0,
				hours: timeGainedOrLost !== 0 ? timeGainedOrLost / 60 : timeGainedOrLost,
				port: cp.port,
				laytimeDetails,
				cargoPortId: cp.id,
				isSettlement,
				...intervals,
			}) as HIIDemurrage;
		} else if (Math.abs(totalDespatch) > 0) {
			let des = ensureNegative(totalDespatch);

			item = getDespatchInvoiceItem(getId, cargo, fixtureCurrency, {
				action,
				amount: des,
				rate: cp.despatch ?? 0,
				hours: timeGainedOrLost !== 0 ? timeGainedOrLost / 60 : timeGainedOrLost,
				port: cp.port,
				laytimeDetails,
				isSettlement,
				cargoPortId: cp.id,
				...intervals,
			}) as HIIDespatch;
		}

		if (item != null) {
			items.push(item);
		}
	});
};

const getAveragedDndItems = ({
	getId,
	cargo,
	items,
	laytimeStats,
	fixtureCurrency,
}: DndLaytimeProps) => {
	let loadingItem: HIIDemurrage | HIIDespatch | null = null;
	let dischargingItem: HIIDemurrage | HIIDespatch | null = null;

	const {
		totalDemurrageLoading,
		totalDespatchLoading,
		totalDemurrageDischarging,
		totalDespatchDischarging,
		totalTimeLoading,
		totalTimeDischarging,
	} = laytimeStats;

	const loadingCargo = {
		...cargo,
		CargoPorts: cargo.CargoPorts.filter((c) => {
			const { action } = splitActionKey(c.portAndActionKey);

			return action === 'loading';
		}),
	};

	const dischargingCargo = {
		...cargo,
		CargoPorts: cargo.CargoPorts.filter((c) => {
			const { action } = splitActionKey(c.portAndActionKey);

			return action === 'discharging';
		}),
	};

	// For we just need any cargoport id belonging to either loading or dischargin for
	// the later PDF generation
	const loadingCargoPortId = loadingCargo?.CargoPorts?.[0]?.id;
	const dischargeCargoPortId = dischargingCargo?.CargoPorts?.[0]?.id;

	const intervals = {
		demurrageDespatchInterval: cargo.demurrageDespatchInterval,
		timeAllowedInterval: cargo.timeAllowedInterval,
	};

	if (
		(totalDemurrageLoading ?? 0) > (totalDespatchLoading ?? 0)
	) {
		let amount = (laytimeStats.totalDemurrageLoading ?? 0) -
			(laytimeStats.totalDespatchLoading ?? 0);

		loadingItem = getDemurrageInvoiceItem(getId, cargo, fixtureCurrency, {
			action: 'loading',
			amount,
			rate: cargo.demurrageLoading ?? 0,
			hours: (totalTimeLoading ?? 0) / 60,
			port: null,
			laytimeDetails: getLaytimeInvoiceLineDescriptions(loadingCargo),
			cargoPortId: loadingCargoPortId,
			isSettlement: laytimeStats.isSettlementLoading,
			...intervals,
		}) as HIIDemurrage;
		items.push(loadingItem);
	} else {
		// Make despatch negative
		let amount = ((laytimeStats.totalDespatchLoading ?? 0) -
			(laytimeStats.totalDemurrageLoading ?? 0)) * -1;

		loadingItem = getDespatchInvoiceItem(getId, cargo, fixtureCurrency, {
			action: 'loading',
			amount,
			rate: cargo.despatchLoading ?? 0,
			hours: (totalTimeLoading ?? 0) / 60,
			port: null,
			laytimeDetails: getLaytimeInvoiceLineDescriptions(loadingCargo),
			cargoPortId: loadingCargoPortId,
			isSettlement: laytimeStats.isSettlementLoading,
			...intervals,
		}) as HIIDespatch;
		items.push(loadingItem);
	}

	if (
		(totalDemurrageDischarging ?? 0) > (totalDespatchDischarging ?? 0)
	) {
		let amount = (laytimeStats.totalDemurrageDischarging ?? 0) -
			(laytimeStats.totalDespatchDischarging ?? 0);

		dischargingItem = getDemurrageInvoiceItem(getId, cargo, fixtureCurrency, {
			action: 'discharging',
			amount,
			rate: cargo.demurrageDischarge ?? 0,
			hours: (totalTimeDischarging ?? 0) / 60,
			port: null,
			laytimeDetails: getLaytimeInvoiceLineDescriptions(dischargingCargo),
			cargoPortId: dischargeCargoPortId,
			isSettlement: laytimeStats.isSettlementDischarging,
			...intervals,
		}) as HIIDemurrage;
		items.push(dischargingItem);
	} else {
		let amount = ((laytimeStats.totalDespatchDischarging ?? 0) -
			(laytimeStats.totalDemurrageDischarging ?? 0)) * -1;

		dischargingItem = getDespatchInvoiceItem(getId, cargo, fixtureCurrency, {
			action: 'discharging',
			amount,
			rate: cargo.despatchDischarge ?? 0,
			hours: (totalTimeDischarging ?? 0) / 60,
			port: null,
			laytimeDetails: getLaytimeInvoiceLineDescriptions(dischargingCargo),
			cargoPortId: dischargeCargoPortId,
			isSettlement: laytimeStats.isSettlementDischarging,
			...intervals,
		}) as HIIDespatch;
		items.push(dischargingItem);
	}
};

export const getDndInvoiceItems = (
	getId: () => number,
	cargo: CargoWithPortsAndCalculationsJSON,
	fixtureCurrency: Values<typeof Currencies>,
	laytimeStats: CargoStats,
	hireInvoices: VoyageInvoice[],
	chartererId: number,
) => {
	const items: (HIIDemurrage | HIIDespatch)[] = [];

	if (laytimeStats.totalReceivable == null || (
		laytimeStats.totalReceivable === 0 &&
		laytimeStats.totalDemurrage === 0 &&
		laytimeStats.totalDespatch === 0
	)) {
		return items;
	}

	switch (cargo.laytimeTerms) {
		case LaytimeTerms.NON_REVERSIBLE: {
			getNonReversibleDndItems({
				cargo,
				fixtureCurrency,
				items,
				getId,
				laytimeStats,
			});
			break;
		}

		case LaytimeTerms.REVERSIBLE: {
			getReversibleDndItems({
				cargo,
				fixtureCurrency,
				items,
				getId,
				laytimeStats,
			});
			break;
		}

		case LaytimeTerms.AVERAGED: {
			getAveragedDndItems({
				cargo,
				fixtureCurrency,
				items,
				getId,
				laytimeStats,
			});
			break;
		}

		default:
			break;
	}

	return items.filter((item) => !isDndLineInvoiced({
		cargo,
		hireInvoices,
		chartererId,
		item,
	}));
};

export const getDemurrageInvoiceItem = (
	getId: () => number,
	cargo: CargoProps & { Brokers: Array<BrokerProps & { BrokerInCargo: BrokerInCargo }> },
	fixtureCurrency: Values<typeof Currencies>,
	params: {
		action: 'discharging' | 'loading' | null;
		rate: number;
		hours: number;
		amount: number;
		port: Port | null;
		laytimeDetails: TemplateItemChildren | null;
		cargoPortId?: number;
		demurrageDespatchInterval?: LaytimeIntervals | null;
		timeAllowedInterval?: LaytimeIntervals | null;
		isSettlement?: boolean;
	},
) => {
	const hiiDemurrage = new HIIDemurrage(getId(), true, {
		cargoId: cargo.id,
		cargoPortId: params.cargoPortId ?? null,
		currency: cargo.currency,
		fixtureCurrency,
		charterer: cargo.charterer,
		terms: cargo.laytimeTerms!,
		demurrageTerm: cargo.demurrageTerm,
		hours: params.hours,
		rate: params.rate,
		laytimeDetails: params.laytimeDetails,
		amount: params.amount,
		type: params.action,
		port: params.port,
		demurrageDespatchInterval: params.demurrageDespatchInterval,
		timeAllowedInterval: params.timeAllowedInterval,
		isSettlement: params.isSettlement,
	});

	hiiDemurrage.children.push(
		...(cargo.addressCommission > 0 ? [new HIIDemurrageAddressCommission(getId(), true, {
			percentage: cargo.addressCommission,
			fixtureCurrency,
			cargoId: cargo.id,
		})] : []),
		...cargo.Brokers
			.filter((b) => {
				return b.BrokerInCargo.paidBy === AccountTypes.CHARTERER;
			})
			.map((c) => new HIIDemurrageBrokerCommission(getId(), true, {
				cargoId: cargo.id,
				brokerId: c.id,
				brokerName: c.name,
				fixtureCurrency,
				percentage: c.BrokerInCargo.commission,
			})),
	);

	hiiDemurrage.children.forEach((c) => {
		c.parentId = hiiDemurrage.id;
	});

	return hiiDemurrage;
};

export const getDespatchInvoiceItem = (
	getId: () => number,
	cargo: CargoProps & { Brokers: Array<BrokerProps & { BrokerInCargo: BrokerInCargo }> },
	fixtureCurrency: Values<typeof Currencies>,
	params: {
		action: 'discharging' | 'loading' | null;
		rate: number;
		hours: number;
		amount: number;
		port: Port | null;
		laytimeDetails: TemplateItemChildren | null;
		cargoPortId?: number;
		demurrageDespatchInterval?: LaytimeIntervals | null;
		timeAllowedInterval?: LaytimeIntervals | null;
		isSettlement?: boolean;
	},
) => {
	return new HIIDespatch(getId(), true, {
		cargoId: cargo.id,
		cargoPortId: params.cargoPortId ?? null,
		currency: cargo.currency,
		fixtureCurrency,
		charterer: cargo.charterer,
		terms: cargo.laytimeTerms!,
		demurrageTerm: cargo.demurrageTerm,
		hours: params.hours,
		rate: params.rate,
		laytimeDetails: params.laytimeDetails,
		amount: params.amount,
		type: params.action,
		port: params.port,
		demurrageDespatchInterval: params.demurrageDespatchInterval,
		timeAllowedInterval: params.timeAllowedInterval,
		isSettlement: params.isSettlement,
	});
};

export const getOffHireHireInvoiceItem = (
	getId: () => number,
	offHire: VoyageOffHirePeriodProps & OffHirePeriodProps & {
		bunkers: Array<VoyageOffHireBunkerProps & BunkerProps>;
	},
	fixtureCurrency: Values<typeof Currencies>,
	params: {
		addressCommission: number | null;
		brokers: ChartererBroker[];
		expensesSubjectToHireDays: ExpenseSubjectToHireDaysProps[];
		calculationMethodForExpensesSubjectToHireDays: Values<typeof CalculationMethodForExpensesSubjectToHireDays>;
		hireDaysDecimals: number;
		useUTC: boolean;
	},
) => {
	const offHireItem = new HIIOffHirePeriod(getId(), true, {
		voyageOffHirePeriodId: offHire.id,
		hirePerDay: offHire.hirePerDay,
		startTime: offHire.startTime,
		endTime: offHire.endTime,
		invoiceNote: offHire.invoiceNote,
		type: offHire.type,
		percentageForOwnersAccount: offHire.percentageForOwnersAccount,
		fixtureCurrency,
		hireDaysDecimals: params.hireDaysDecimals,
		useUTC: offHire.useInvoiceTimeFormat ?
			params.useUTC :
			offHire.selectedTimeFormat === TimeZoneFormat.UTC,
	});

	offHireItem.children.push(
		...(params.addressCommission ? [new HIIOffHireAddressCommission(getId(), true, {
			percentage: params.addressCommission,
			fixtureCurrency,
		})] : []),
		...params.brokers.map((c) => new HIIOffHireBrokerCommission(getId(), true, {
			brokerId: c.id,
			brokerName: c.name,
			percentage: c.commission,
			fixtureCurrency,
		})),
		...params.expensesSubjectToHireDays.map((e) => new HIIOffHireExpenseSubjectToHireDays(getId(), true, {
			fixtureCurrency,
			name: e.name,
			amount: e.amount,
			interval: e.interval,
			calculationMethodForExpensesSubjectToHireDays: params.calculationMethodForExpensesSubjectToHireDays,
		})),
		...offHire.bunkers.map((bunker) => new HIIOffHireBunker(getId(), true, {
			bunkerId: bunker.id,
			quantity: bunker.quantity,
			pricePerTon: bunker.pricePerTon,
			fuelGrade: bunker.fuelGrade,
			currency: bunker.currency,
			exchangeRate: bunker.exchangeRate,
			fixtureCurrency,
		})),
	);

	offHireItem.children.forEach((c) => {
		c.parentId = offHireItem.id;
	});

	return offHireItem;
};

export const getRevenueItemHireInvoiceItem = (
	getId: () => number,
	revenueItem: RevenueItemProps,
	cargo: CargoWithPortsAndCalculationsJSON,
	fixtureCurrency: Values<typeof Currencies>,
) => {
	const item = new HIIRevenueItem(getId(), true, {
		cargoId: revenueItem.cargoId,
		revenueItemId: revenueItem.id,
		voyageId: revenueItem.voyageId,
		amount: revenueItem.amount,
		itemDescription: revenueItem.itemDescription,
		currency: revenueItem.currency,
		exchangeRate: revenueItem.exchangeRate,
		note: revenueItem.note || null,
		chartererId: revenueItem.chartererId,
		subjectToCommissions: revenueItem.subjectToCommissions,
		fixtureCurrency,
	});

	if (item.subjectToCommissions && cargo != null) {
		item.children.push(
			...(cargo.addressCommission > 0 ? [new HIIDemurrageAddressCommission(getId(), true, {
				cargoId: cargo.id,
				percentage: cargo.addressCommission,
				fixtureCurrency,
			})] : []),
			...cargo.Brokers
				.filter((b) => {
					return b.BrokerInCargo.paidBy === AccountTypes.CHARTERER;
				})
				.map((c) => new HIIDemurrageBrokerCommission(getId(), true, {
					cargoId: cargo.id,
					brokerId: c.id,
					brokerName: c.name,
					percentage: c.BrokerInCargo.commission,
					fixtureCurrency,
				})),
		);

		item.children.forEach((c) => {
			c.parentId = item.id;
		});
	}

	return item;
};

export const getBbHireInvoiceItem = (
	getId: () => number,
	bbItem: BbItemProps,
	fixtureCurrency: Values<typeof Currencies>,
	from: Moment,
	to: Moment,
	params: {
		addressCommission: number | null;
		daysDecimals: number;
		brokers: ChartererBroker[];
	},
	useUTC?: boolean,
) => {
	if (!bbItem.id) {
		throw new Error('bbItem.id is null or undefined');
	}

	const item = new HIIBbItem(getId(), true, {
		bbItemId: bbItem.id,
		amount: bbItem.amount,
		pcm: bbItem.pcm,
		fixtureCurrency,
		brokerPaidByChartererIds: bbItem.brokerPaidByChartererIds,
		brokerPaidByChartererPercentages: bbItem.brokerPaidByChartererPercentages,
		brokerPaidByOwnerIds: bbItem.brokerPaidByOwnerIds,
		brokerPaidByOwnerPercentages: bbItem.brokerPaidByOwnerPercentages,
		addComm: bbItem.addComm,
		type: bbItem.type,
		from,
		to,
		useUTC,
		daysDecimals: params.daysDecimals,
	});

	item.children.push(
		...(params.addressCommission && params.addressCommission > 0 ? [new HIIBbItemAddressCommission(getId(), true, {
			bbItemId: bbItem.id,
			fixtureCurrency,
			percentage: params.addressCommission,
		})] : []),
		...params.brokers.map((c) => new HIIBbItemBrokerCommission(getId(), true, {
			bbItemId: bbItem.id,
			brokerId: c.id,
			brokerName: c.name,
			fixtureCurrency,
			percentage: c.commission ?? 0,
		})),
	);

	item.children.forEach((c) => {
		c.parentId = item.id;
	});

	return item;
};
